/**
 * Father's Day gift tags — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_may_june`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const fathersDayGiftTagsPack: ToilePack = {
    slug: 'fathers-day-gift-tags',
    category: 'season',
    name: "Father's Day gift tags",
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'classic', label: 'Classic tags', subject: 'a sheet of six rectangular tags with a punched hole, each printed "Happy Father\'s Day" above a small bow tie and moustache'},
        {key: 'pun', label: 'Pun tags', subject: 'a sheet of round tags reading "You\'re Tee-rific, Dad" with a golf ball on a tee drawn beneath'},
        {key: 'tofrom', label: 'To and from', subject: 'a sheet of eight luggage-shaped tags with a small hammer in one corner and "To: Dad" and "From:" printed above a blank line'},
    ],
    styles: [
        {key: 'kraft', label: 'Kraft paper', style: 'tags in white and dark ink as if printed on kraft paper, hand-stamped look, simple line motifs', palette: 'kraft brown with white and navy ink', framing: 'Flat on the sheet, evenly spaced'},
        {key: 'flat', label: 'Flat color', style: 'flat vector illustration, bold clean shapes, smooth gradient-free fills, crisp lettering', palette: 'navy, burnt orange, green and cream, four colors', framing: 'Full page on a solid cream ground with an even margin'},
        {key: 'lineart', label: 'Line art', style: 'fine black line art, uniform thin stroke, closed shapes, no fills, simple hand lettering', palette: 'black ink only', framing: 'Drawn on plain white paper with a clean margin'},
    ],
    directions: ['Bottle tag', 'Gold foil', 'Red palette'],
    directionHint: 'try: Bottle tag',
    seo: {
        title: "Father's Day Gift Tags: Classic, Pun, To and From",
        metaDescription: "Printable Father's Day gift tags to cut out and tie on: classic, pun and to-and-from tags, in three styles.",
        h1: "Father's Day gift tags: classic, pun, to and from",
        tagline: 'Nine sheets of cut-out tags with the greeting printed and a hole for string, in three styles.',
        intro: "Printable tags to tie on a Father's Day present: a bottle, a mug, a box of sweets or a wrapped tool. Classic tags print Happy Father's Day above a bow tie, pun tags say You're Tee-rific, Dad beside a golf ball on a tee, and to-and-from tags have To: Dad and From lines. Kraft paper, flat color or line art. Type a different greeting or gift, and the same tag layout carries it.",
        useCases: [
            'a tag on a bottle given to Dad',
            'a tag on a mug or a box of sweets',
            'a tag on a wrapped tool',
            "a label for a Father's Day card envelope",
        ],
        faq: [
            {q: 'How are the tags laid out?', a: 'The tags sit in rows with a margin around the sheet and space between them to cut.'},
            {q: 'Which tags have a name line?', a: 'The to-and-from tags have To: Dad and From lines to fill in.'},
            {q: 'Can the pun be changed?', a: 'Yes. Type a different pun or greeting, and the same layout carries it.'},
        ],
    },
}
